// Story 5.10 — Aftermarket offers/bidding hooks (share + sealed-dossier).
//
// Consolidated because every offer hook shares the same typed-error
// envelope, idempotency-key generation, and {share | sealed-dossier} path
// swap. Mutations deliberately omit onSuccess invalidation — the consuming
// surface (MakeOfferDialog / MyListingsPage) owns invalidation because it
// knows which queries to refresh.
//
// Endpoints:
//   POST /api/v1/marketplace/listings/{kind}/:listing_id/offers   — place
//   GET  /api/v1/marketplace/listings/{kind}/:listing_id/offers   — seller list
//   POST /api/v1/marketplace/offers/{kind}/:offer_id/accept|reject|withdraw
//   GET  /api/v1/marketplace/me/{kind}-offers                     — buyer's own

import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type OfferKind = "share" | "sealed_dossier";

/** URL segment for an offer kind. */
export function offerKindPath(kind: OfferKind): string {
  return kind === "share" ? "share" : "sealed-dossier";
}

export interface OfferFeeBreakdown {
  platform_fee_cents: number;
  creator_royalty_cents: number;
  estimated_proceeds_cents: number;
}

export interface PlaceOfferResponseData {
  offer_id: string;
  listing_id: string;
  item_id: string;
  amount_cents: number;
  status: string;
  fee_breakdown: OfferFeeBreakdown;
  balance_after_cents: number;
}

export interface AcceptOfferResponseData {
  offer_id: string;
  listing_id: string;
  item_id: string;
  price_cents: number;
  status: string;
  fee_breakdown: OfferFeeBreakdown;
  vault_redirect_url: string;
}

export interface OfferDecisionResponseData {
  offer_id: string;
  listing_id: string;
  status: string;
  amount_cents: number;
  balance_after_cents: number;
}

/** One offer as the SELLER sees it (anonymous — amount only). */
export interface ListingOfferItem {
  offer_id: string;
  amount_cents: number;
  created_at: string;
}

/** One offer as the BIDDER sees it (their own offers). */
export interface MyOfferItem {
  offer_id: string;
  listing_id: string;
  item_id: string;
  amount_cents: number;
  status: string;
  listing_price_cents: number;
  listing_status: string;
  created_at: string;
  decided_at: string | null;
}

export interface OfferError {
  code: string;
  message: string;
  field?: string;
  required_cents?: number;
  available_cents?: number;
  status: number;
}

function freshIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function parseOfferError(
  response: Response,
  fallbackCode: string,
): Promise<OfferError> {
  let detail: OfferError = {
    code: fallbackCode,
    message: `Request failed (HTTP ${response.status})`,
    status: response.status,
  };
  try {
    const raw = await response.json();
    if (raw?.error) {
      detail = {
        code: raw.error.code,
        message: raw.error.message,
        field: raw.error.field,
        required_cents:
          raw.error.required_cents ?? raw.error.details?.required_cents,
        available_cents:
          raw.error.available_cents ?? raw.error.details?.available_cents,
        status: response.status,
      };
    }
  } catch {
    // Body not JSON — keep default.
  }
  return detail;
}

// ---- Place offer --------------------------------------------------------

export interface PlaceOfferVariables {
  listing_id: string;
  amount_cents: number;
}

async function postPlaceOffer(
  kind: OfferKind,
  variables: PlaceOfferVariables,
): Promise<PlaceOfferResponseData> {
  const response = await fetch(
    `/api/v1/marketplace/listings/${offerKindPath(kind)}/${variables.listing_id}/offers`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "x-idempotency-key": freshIdempotencyKey(),
      },
      body: JSON.stringify({ amount_cents: variables.amount_cents }),
    },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw await parseOfferError(response, "PLACE_OFFER_FAILED");
  }
  const body = (await response.json()) as { data: PlaceOfferResponseData };
  return body.data;
}

export function usePlaceOffer(
  kind: OfferKind,
): UseMutationResult<PlaceOfferResponseData, OfferError, PlaceOfferVariables> {
  return useMutation<PlaceOfferResponseData, OfferError, PlaceOfferVariables>({
    mutationFn: (variables) => postPlaceOffer(kind, variables),
  });
}

// ---- Accept / reject / withdraw -----------------------------------------

async function postOfferDecision<T>(
  kind: OfferKind,
  offerId: string,
  action: "accept" | "reject" | "withdraw",
  fallbackCode: string,
  payload: Record<string, unknown> = {},
): Promise<T> {
  const response = await fetch(
    `/api/v1/marketplace/offers/${offerKindPath(kind)}/${offerId}/${action}`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "x-idempotency-key": freshIdempotencyKey(),
      },
      body: JSON.stringify(payload),
    },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw await parseOfferError(response, fallbackCode);
  }
  const body = (await response.json()) as { data: T };
  return body.data;
}

export function useAcceptOffer(
  kind: OfferKind,
): UseMutationResult<AcceptOfferResponseData, OfferError, { offer_id: string }> {
  return useMutation<AcceptOfferResponseData, OfferError, { offer_id: string }>({
    mutationFn: ({ offer_id }) =>
      postOfferDecision<AcceptOfferResponseData>(
        kind,
        offer_id,
        "accept",
        "ACCEPT_OFFER_FAILED",
      ),
  });
}

export function useRejectOffer(
  kind: OfferKind,
): UseMutationResult<
  OfferDecisionResponseData,
  OfferError,
  { offer_id: string; block?: boolean }
> {
  return useMutation<
    OfferDecisionResponseData,
    OfferError,
    { offer_id: string; block?: boolean }
  >({
    mutationFn: ({ offer_id, block }) =>
      postOfferDecision<OfferDecisionResponseData>(
        kind,
        offer_id,
        "reject",
        "REJECT_OFFER_FAILED",
        { block: block ?? false },
      ),
  });
}

export function useWithdrawOffer(
  kind: OfferKind,
): UseMutationResult<
  OfferDecisionResponseData,
  OfferError,
  { offer_id: string }
> {
  return useMutation<OfferDecisionResponseData, OfferError, { offer_id: string }>(
    {
      mutationFn: ({ offer_id }) =>
        postOfferDecision<OfferDecisionResponseData>(
          kind,
          offer_id,
          "withdraw",
          "WITHDRAW_OFFER_FAILED",
        ),
    },
  );
}

// ---- Seller: offers on my listing ---------------------------------------

async function fetchListingOffers(
  kind: OfferKind,
  listingId: string,
): Promise<ListingOfferItem[]> {
  const response = await fetch(
    `/api/v1/marketplace/listings/${offerKindPath(kind)}/${listingId}/offers`,
    { method: "GET", credentials: "include" },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw await parseOfferError(response, "LIST_OFFERS_FAILED");
  }
  const body = (await response.json()) as {
    data?: { offers?: ListingOfferItem[] };
  };
  return body.data?.offers ?? [];
}

export function useListingOffers(
  kind: OfferKind,
  listingId: string,
  enabled = true,
): UseQueryResult<ListingOfferItem[], OfferError> {
  return useQuery<ListingOfferItem[], OfferError>({
    queryKey: ["listing-offers", kind, listingId],
    queryFn: () => fetchListingOffers(kind, listingId),
    enabled,
    staleTime: 15 * 1000,
    retry: false,
  });
}

// ---- Buyer: my own offers -----------------------------------------------

async function fetchMyOffers(kind: OfferKind): Promise<MyOfferItem[]> {
  const response = await fetch(
    `/api/v1/marketplace/me/${offerKindPath(kind)}-offers`,
    { method: "GET", credentials: "include" },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw await parseOfferError(response, "MY_OFFERS_FAILED");
  }
  const body = (await response.json()) as { data?: { offers?: MyOfferItem[] } };
  return body.data?.offers ?? [];
}

export function useMyOffers(
  kind: OfferKind,
): UseQueryResult<MyOfferItem[], OfferError> {
  return useQuery<MyOfferItem[], OfferError>({
    queryKey: ["my-offers", kind],
    queryFn: () => fetchMyOffers(kind),
    staleTime: 15 * 1000,
    retry: false,
  });
}
